import { useCallback, useRef } from "react";
import {
  StyledColumnResizeGuide,
  StyledColumnResizeOverlay,
  StyledColumnResizeHandle,
} from "./Table.styles";

/**
 * Shared column resize handle (pointer, keyboard, double-click auto-fit).
 */
export const ColumnResizeHandle = ({
  columnId,
  thElement,
  tableElement,
  ariaValueNow,
  onResizePointerDown,
  onAutoFit,
  onKeyboardResize,
  className,
  ...rest
}) => {
  const lastClickRef = useRef(0);

  const handlePointerDown = e => {
    const now = Date.now();
    if (now - lastClickRef.current < 300) return;
    lastClickRef.current = now;
    onResizePointerDown?.(e);
  };

  const handleDoubleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    onAutoFit?.();
  };

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter") {
        e.preventDefault();
        onAutoFit?.();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onKeyboardResize?.(e.shiftKey ? -16 : -8, thElement);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        onKeyboardResize?.(e.shiftKey ? 16 : 8, thElement);
      }
    },
    [onAutoFit, onKeyboardResize, thElement],
  );

  return (
    <StyledColumnResizeHandle
      {...rest}
      className={className}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={ariaValueNow}
      aria-valuemin={60}
      aria-valuemax={600}
      aria-label={`Resize ${columnId} column`}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      data-column-resize={columnId}
    />
  );
};

export const ColumnResizeDragOverlay = ({ dragOverlay }) => {
  if (!dragOverlay?.tableRect) return null;
  const { clientX, width, tableRect } = dragOverlay;
  const top = tableRect.top;
  const height = tableRect.height;

  return (
    <StyledColumnResizeOverlay>
      <StyledColumnResizeGuide style={{ left: clientX, top, height }} />
      <span
        className="column-resize-readout"
        style={{ left: clientX + 8, top: top + 4 }}
      >
        {width}px
      </span>
    </StyledColumnResizeOverlay>
  );
};
